import * as yup from 'yup';
import UtilityService from '../../services/UtilityService';
import { UIConstants } from '../../utils/constants';
import {
  getOptionalStringValidator,
  getRequiredNumberValidator,
  getRequiredStringValidator,
} from './Generators';

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const poNumberSchema = getRequiredStringValidator().test(
  'poNumberValid',
  UIConstants.NO_BLANKS_OR_SPECIAL_CHARACTERS,
  value => {
    if (!value?.match(/^[a-z0-9]+$/i)) {
      return false;
    }
    return true;
  }
);

export const guestCheckoutPoNumberSchema = yup.object().shape({
  poNumber: poNumberSchema,
});

export const giftCardSchema = yup.object().shape({
  poNumber: yup
    .string()
    .length(13, UIConstants.WRONG_LENGTH_GIFT_CARD)
    .required(),
});

export const cardNumberSchema = getRequiredStringValidator().test(
  'cardNumberValid',
  UIConstants.INVALID_CARD_NUMBER,
  value => {
    if (!value?.startsWith('****')) {
      return UtilityService.validateCreditCardNumber(value ?? '');
    }
    return true;
  }
);
export const cardHolderNameSchema = getRequiredStringValidator();
export const expMonthSchema = getRequiredNumberValidator().min(1).max(12);
export const expYearSchema = getRequiredNumberValidator()
  .min(currentYear)
  .max(currentYear + 20);
export const cvvSchema = getRequiredStringValidator().test(
  'isCvv',
  UIConstants.WRONG_LENGTH_CVV,
  (value: string | null | undefined) => {
    return Boolean(value && value.match(/^\d{3,4}$/)?.length === 1);
  }
);

const getCreditCardSchema = (
  currentMonth: number,
  isSpreedlyEnabled: boolean
) =>
  yup.object().shape({
    cardNumber: isSpreedlyEnabled
      ? getOptionalStringValidator()
      : cardNumberSchema,
    cardHolderName: cardHolderNameSchema,
    expMonth: expMonthSchema.when('expYear', {
      is: (expYear: number) => expYear === currentYear,
      then: yup
        .number()
        .required()
        .min(currentMonth, UIConstants.PAST_EXP_DATE)
        .max(12),
      otherwise: expMonthSchema,
    }),
    expYear: expYearSchema,
    cvv: isSpreedlyEnabled ? getOptionalStringValidator() : cvvSchema,
  });
export const getPaymentSchema = (
  currentMonth: number,
  isAcpCheckout?: boolean,
  isPaymentRequired?: boolean,
  isSpreedlyEnabled = false
): yup.ObjectSchema<
  yup.Shape<
    // eslint-disable-next-line @typescript-eslint/ban-types
    object | undefined,
    {
      creditCard:
        | {
            cardNumber: string | undefined;
            cardHolderName: string | undefined;
            expMonth: number | undefined;
            expYear: number | undefined;
            cvv: string | undefined;
          }
        | {
            cardHolderName: string | undefined;
            expMonth: number | undefined;
            expYear: number | undefined;
          }
        | undefined
        // eslint-disable-next-line @typescript-eslint/ban-types
        | object;
      paymentMethodId: string | undefined;
      giftCard:
        | {
            code: string | undefined;
          }
        | undefined
        // eslint-disable-next-line @typescript-eslint/ban-types
        | object;
      purchaseOrder:
        | {
            poNumber: string | undefined;
          }
        | undefined
        // eslint-disable-next-line @typescript-eslint/ban-types
        | object;
    }
  >,
  // eslint-disable-next-line @typescript-eslint/ban-types
  object
> =>
  yup.object().shape({
    paymentMethodId: isPaymentRequired
      ? yup.string().required()
      : yup.string().optional(),
    creditCard: yup.object().when('paymentMethodId', {
      is: (paymentMethodId: string) =>
        paymentMethodId === '-1' && !isAcpCheckout && isPaymentRequired,
      then: getCreditCardSchema(currentMonth, isSpreedlyEnabled),
      otherwise: yup.object().optional(),
    }),
    giftCard: yup.object().when('paymentMethodId', {
      is: (paymentMethodId: string) => paymentMethodId === '-2',
      then: giftCardSchema,
      otherwise: yup.object().optional(),
    }),
    purchaseOrder: yup.object().when('paymentMethodId', {
      is: (paymentMethodId: string) => paymentMethodId === '13',
      then: guestCheckoutPoNumberSchema,
      otherwise: yup.object().optional(),
    }),
  });
